import {
  BlockNoteEditor,
  BlockSchema,
  InlineContentSchema,
  insertOrUpdateBlock,
  StyleSchema,
} from '@blocknote/core';
import {
  createReactBlockSpec,
  DefaultReactSuggestionItem,
  ReactCustomBlockRenderProps,
} from '@blocknote/react';
import { getEditorMode } from 'utils/createEditorWithMode';
import { ReactElement } from 'react';
import { useBlockDelete } from 'hooks/useBlockDelete';
import { StarIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { TeacherSABlock } from './teacher/TeacherSABlock';
import { StudentSABlock } from './student/StudentSABlock';
import { ReviewSABlock } from './reviewer/ReviewSABlock';
import { BlockBorderBox } from '../common/BlockBorderBox';

export type SABlockProps = ReactCustomBlockRenderProps<
  (typeof SelfAssessmentBlock)['config'],
  InlineContentSchema,
  StyleSchema
>;

const propSchema = {
  score: {
    default: -1,
  },
  minScore: {
    default: 0,
  },
  maxScore: {
    default: 5,
  },
  question: {
    default: '',
    type: 'string',
    description: 'The question text',
  },
  isSubmitted: {
    type: 'boolean',
    default: false,
  },
};

const SelfAssessmentBlockContent = ({ props }: { props: SABlockProps }): ReactElement => {
  const { editor, block } = props;
  const mode = getEditorMode(editor);

  const { isDeleting, handleDelete } = useBlockDelete({
    editor,
    block,
    name: 'Selbsteinschätzung',
  });

  const handleSeparatorClick = (): void => {
    editor.focus();
    editor.setTextCursorPosition(block, 'end');
    // Simulate Enter key press to create a new block
    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
      composed: true,
    });
    editor.domElement?.dispatchEvent(enterEvent);
    editor.openSuggestionMenu('/');
  };

  const getBlockByMode = (): ReactElement | null => {
    switch (mode) {
      case 'author':
        return <TeacherSABlock {...props} onDelete={handleDelete} />;
      case 'student':
        return <StudentSABlock {...props} />;
      case 'review':
        return <ReviewSABlock {...props} />;
      default:
        return null;
    }
  };

  return (
    <BlockBorderBox
      isDeleting={isDeleting}
      onSeparatorClick={mode === 'author' ? handleSeparatorClick : undefined}
      shouldShowSeparator={mode === 'author'}
      marginTop={{ base: '15px', md: '20px' }}
      marginBottom={{ base: '10px', md: '15px' }}
    >
      {getBlockByMode()}
    </BlockBorderBox>
  );
};

const SelfAssessmentBlock = createReactBlockSpec(
  {
    type: 'selfAssessment',
    propSchema,
    content: 'inline',
  },
  {
    render: (props) => <SelfAssessmentBlockContent props={props} />,
  }
);

const createSelfAssessmentSlashItem = <T extends BlockSchema>(
  editor: BlockNoteEditor<T>
): DefaultReactSuggestionItem => ({
  title: 'Selbsteinschätzung',
  onItemClick: () =>
    insertOrUpdateBlock(editor, {
      type: 'selfAssessment',
    }),
  aliases: ['selfAssessment', 'sa'],
  group: 'Interaktiv',
  icon: <NovaIcon icon={StarIcon} size="m" />,
  subtext: 'Erstelle eine Selbsteinschätzung für die Lernenden',
});

export { SelfAssessmentBlock, createSelfAssessmentSlashItem };
